import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, startWith, switchMap, take } from 'rxjs/operators';
import { ChallengeContextService } from '../../shared/contexts/challenge-context/challenge-context.service';
import { AppMetaData, ChallengeTeamEntity, ChallengeTeamJoinRequestStatusAllItems, ChallengeTeamMemberEntity, ChallengeTeamJoinRequestEntity, UserProfileEntity, UserChallengeEntity, ChallengeDefinitionEntity } from '../../../meta-data/app-meta-data.service';
import { challengeHasStarted } from '../models/team-card.models';

import { CentralDataService } from '../../streaker-central/services/central-data.service';

export interface DiscoverableTeam {
    team: ChallengeTeamEntity;
    isMember: boolean;
    isCaptain: boolean;
    isPending: boolean;
    isApproved: boolean;
}
export interface TeamDiscoveryState {
    loading: boolean;
    error: string;
    challengeName: string;
    challengeId: number;
    registered: boolean;
    userChallengeId: number;
    canJoin: boolean;
    activitiesStarted: boolean;
    teams: DiscoverableTeam[];
}
const initial = (): TeamDiscoveryState => ({ loading: true, error: '', challengeName: '', challengeId: 0, registered: false, userChallengeId: 0, canJoin: false, activitiesStarted: false, teams: [] });

/** Page-scoped reads. Switching challenge/account cancels earlier result streams. */
@Injectable()
export class TeamDiscoveryStateService implements OnDestroy {
    private subject = new BehaviorSubject<TeamDiscoveryState>(initial());
    readonly state$ = this.subject.asObservable();
    private subscriptions = new Subscription();

    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private security: DwSecurityUserService, selection: ChallengeContextService, private data: CentralDataService) {
        this.subscriptions.add(data.teamChanged$.subscribe(change => {
            const current = this.subject.value;
            if (!current.challengeId || current.loading) { return; }
            this.subject.next({ ...current, teams: current.teams.map(row => {
                if (change.teamId && row.team.ChallengeTeamId !== change.teamId) { return row; }
                const own = change.member?.UserChallengeId === current.userChallengeId;
                return { ...row, team: change.team ? { ...row.team, ...change.team } : row.team,
                    ...(own ? { isMember: !change.removed, isCaptain: !change.removed && !!change.member.IsTeamAdmin } : {}) };
            }).filter(row => !(change.removed && change.team && row.team.ChallengeTeamId === change.teamId)
                && (row.team.IsPrivateTeam !== true || row.isMember)) });
        }));
        this.subscriptions.add(combineLatest([selection.challengeChange$,
            security.securityContextChanged().pipe(startWith(null)), data.changed$.pipe(startWith(null))])
            .pipe(switchMap(([challenge]) => {
                this.subject.next({ ...initial(), challengeName: challenge.ChallengeName });
                if (!(challenge.ChallengeDefinitionId > 0)) {
                    return of({ ...initial(), loading: false, error: 'Select a challenge to discover teams.' });
                }
                const user = this.security.securityContext && this.security.securityContext.ApplicationUser;
                if (!user || !user.UserId) {
                    return of({ ...initial(), loading: false, error: 'Sign in to discover teams.' });
                }
                const challengeId = challenge.ChallengeDefinitionId;
                const publicTeams = this.loadPublicTeams(challengeId);
                const ownStatus = this.loadUserProfiles(user.UserId)
                    .pipe(switchMap(profiles => {
                        if (!profiles.length) { return of([]); }
                        if (profiles.length !== 1) { throw new Error('Ambiguous user profile.'); }
                        return this.loadCompletedEnrollments(profiles[0].UserProfileId, challengeId);
                    }), switchMap(enrollments => {
                        if (!enrollments.length) { return of({ registered: false, userChallengeId: 0, members: [], pending: [], owned: [] }); }
                        if (enrollments.length !== 1) { throw new Error('Ambiguous challenge enrollment.'); }
                        const userChallengeId = enrollments[0].UserChallengeId;
                        return combineLatest([
                            this.loadMemberships(userChallengeId),
                            this.loadJoinRequests(userChallengeId),
                        ]).pipe(switchMap(([members, pending]) => {
                            const ids = Array.from(new Set<number>(members.map(member => member.ChallengeTeamId)));
                            const ownTeams = ids.length ? this.loadOwnedTeams(challengeId, ids) : of([]);
                            return ownTeams.pipe(map(owned => ({ registered: true, userChallengeId, members, pending, owned })));
                        }));
                    }));
                const definition = this.loadDefinition(challengeId);
                return combineLatest([publicTeams, ownStatus, definition]).pipe(map(([publicRows, own, definitions]): TeamDiscoveryState => {
                    const unique = new Map<number, ChallengeTeamEntity>();
                    [...publicRows, ...own.owned].forEach(team => unique.set(team.ChallengeTeamId, team));
                    const teams = Array.from(unique.values()).map(team => ({ team,
                        isMember: own.members.some(member => member.ChallengeTeamId === team.ChallengeTeamId),
                        isCaptain: own.members.some(member => member.ChallengeTeamId === team.ChallengeTeamId && member.IsTeamAdmin === true),
                        isPending: own.pending.some(request => request.ChallengeTeamId === team.ChallengeTeamId &&
                            request.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending),
                        isApproved: own.pending.some(request => request.ChallengeTeamId === team.ChallengeTeamId &&
                            request.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Approved),
                    })).sort((a, b) => (a.team.TeamName || '').localeCompare(b.team.TeamName || '') ||
                        a.team.ChallengeTeamId - b.team.ChallengeTeamId);
                    return { loading: false, error: '', challengeName: challenge.ChallengeName, challengeId, registered: own.registered,
                        userChallengeId: own.userChallengeId, canJoin: own.registered && definitions.length === 1 && definitions[0].IsCurrent === true,
                        activitiesStarted: definitions.length === 1 && challengeHasStarted(definitions[0].ChallengeStartDate), teams };
                }), catchError(() => of({ ...initial(), loading: false, challengeName: challenge.ChallengeName,
                    error: 'Unable to load teams and membership status. Please try again.' })));
            })).subscribe(state => this.subject.next(state)));
    }
    refresh(): void { this.data.invalidate(); }
    private loadPublicTeams(challengeId: number): Observable<ChallengeTeamEntity[]> {
        return this.data.publicTeams(challengeId);
    }
    private loadOwnedTeams(challengeId: number, teamIds: number[]): Observable<ChallengeTeamEntity[]> {
        return this.data.memberTeams(challengeId, teamIds);
    }
    private loadUserProfiles(userId: number): Observable<UserProfileEntity[]> {
        return this.data.userProfile(userId);
    }
    private loadCompletedEnrollments(profileId: number, challengeId: number): Observable<UserChallengeEntity[]> {
        return this.data.enrollment(profileId, challengeId).pipe(map(rows => rows.filter(row => row.IsRegistrationComplete === true)));
    }
    private loadMemberships(enrollmentId: number): Observable<ChallengeTeamMemberEntity[]> {
        return this.data.memberships(enrollmentId);
    }
    private loadJoinRequests(enrollmentId: number): Observable<ChallengeTeamJoinRequestEntity[]> {
        return this.data.joinRequests(enrollmentId);
    }
    private loadDefinition(challengeId: number): Observable<ChallengeDefinitionEntity[]> {
        return this.data.definitions().pipe(take(1), map(rows => rows.filter(row => row.ChallengeDefinitionId === challengeId)));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
